import { createContext, createElement, useContext, useRef, type ReactNode } from 'react';
import { createStore } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;
const ZOOM_STEP = 1.25;

interface PreviewZoomState {
  zoom: number;
  pan: { x: number; y: number };
  /** True while any pan-affecting interaction is in progress (right-click drag, minimap drag). Drives transform-transition suppression so the image tracks the pointer instead of easing. */
  isPanning: boolean;
}

interface PreviewZoomActions {
  setZoom: (z: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  setPan: (p: { x: number; y: number }) => void;
  setPanning: (p: boolean) => void;
  reset: () => void;
}

type PreviewZoomStore = PreviewZoomState & PreviewZoomActions;

const INITIAL: PreviewZoomState = {
  zoom: 1,
  pan: { x: 0, y: 0 },
  isPanning: false,
};

function clampZoom(z: number): number {
  if (z < MIN_ZOOM) return MIN_ZOOM;
  if (z > MAX_ZOOM) return MAX_ZOOM;
  return z;
}

// Per-pane factory: each pane (e.g. compare-versions panes) gets its own
// store instance so zoom/pan/panning never crosses pane boundaries.
function createPreviewZoomStore() {
  return createStore<PreviewZoomStore>((set) => ({
    ...INITIAL,
    setZoom: (z) => {
      const zoom = clampZoom(z);
      set(zoom <= 1 ? { zoom: 1, pan: { x: 0, y: 0 } } : { zoom });
    },
    zoomIn: () => set((s) => ({ zoom: clampZoom(s.zoom * ZOOM_STEP) })),
    zoomOut: () =>
      set((s) => {
        const zoom = clampZoom(s.zoom / ZOOM_STEP);
        return zoom <= 1 ? { zoom: 1, pan: { x: 0, y: 0 } } : { zoom };
      }),
    setPan: (p) => set((s) => (s.pan.x === p.x && s.pan.y === p.y ? {} : { pan: p })),
    setPanning: (p) => set((s) => (s.isPanning === p ? {} : { isPanning: p })),
    reset: () => set(INITIAL),
  }));
}

type PreviewZoomStoreApi = ReturnType<typeof createPreviewZoomStore>;

const PreviewZoomContext = createContext<PreviewZoomStoreApi | null>(null);

export function PreviewZoomProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<PreviewZoomStoreApi | null>(null);
  if (!storeRef.current) storeRef.current = createPreviewZoomStore();
  return createElement(PreviewZoomContext.Provider, { value: storeRef.current }, children);
}

function usePreviewZoomStoreApi(): PreviewZoomStoreApi {
  const ctx = useContext(PreviewZoomContext);
  if (!ctx) throw new Error('Preview zoom hooks must be used inside <PreviewZoomProvider>');
  return ctx;
}

export const usePreviewZoom = () => {
  const store = usePreviewZoomStoreApi();
  return useStoreWithEqualityFn(
    store,
    (s) => ({ zoom: s.zoom, zoomIn: s.zoomIn, zoomOut: s.zoomOut, setZoom: s.setZoom }),
    shallow,
  );
};

export const usePreviewPan = () => {
  const store = usePreviewZoomStoreApi();
  return useStoreWithEqualityFn(store, (s) => ({ pan: s.pan, setPan: s.setPan }), shallow);
};

export const usePreviewIsPanning = () => {
  const store = usePreviewZoomStoreApi();
  return useStoreWithEqualityFn(store, (s) => s.isPanning, Object.is);
};

export const usePreviewSetPanning = () => {
  const store = usePreviewZoomStoreApi();
  return useStoreWithEqualityFn(store, (s) => s.setPanning, Object.is);
};

export const usePreviewZoomReset = () => {
  const store = usePreviewZoomStoreApi();
  return useStoreWithEqualityFn(store, (s) => s.reset, Object.is);
};
